import {
  Avatar,
  Button,
  Card,
  Column,
  DataStatus,
  Dropdown,
  DropdownItem,
  Pagination,
  Search,
  Separator,
  Table,
  TableRow,
} from 'platform/components';
import {Box, HStack, Text, VStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {ReactNode, useState} from 'react';

import {LeaderboardUser, PayPerUsePeriod} from '@dms/api/settings';
import i18n from '@dms/i18n';

import {downloadBlobFile, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {
  LEADERBOARD_ROWS_PER_PAGE,
  LEADERBOARD_ROWS_PER_PAGE_OPTIONS,
} from '../constants/LEADERBOARD_ROWS_PER_PAGE';
import {buildLeaderboardCsv} from '../utils/buildLeaderboardCsv';
import {buildLeaderboardExportFilename} from '../utils/buildLeaderboardExportFilename';
import {
  buildLeaderboardExportTable,
  LabeledOperationColumn,
} from '../utils/buildLeaderboardExportTable';
import {filterLeaderboardUsers} from '../utils/filterLeaderboardUsers';
import {getLeaderboardColumnTotals} from '../utils/getLeaderboardColumnTotals';
import {getLeaderboardStats} from '../utils/getLeaderboardStats';
import {PayPerUseOperationColumn} from '../utils/getOperationTypeColumns';
import {getOperationTypeLabel} from '../utils/getOperationTypeLabel';
import {getLeaderboardPageCount, paginateLeaderboardUsers} from '../utils/paginateLeaderboardUsers';
import {
  PayPerUseLeaderboardSort,
  sortLeaderboardUsers,
  TOTAL_COLUMN_ID,
} from '../utils/sortLeaderboardUsers';
import {zeroFillUserOperationCounts} from '../utils/zeroFillUserOperationCounts';
import {
  ColumnVisibilityItem,
  PayPerUseColumnVisibilityDropdown,
} from './PayPerUseColumnVisibilityDropdown';
import {PayPerUseSortableHeader} from './PayPerUseSortableHeader';

// Users are sorted by total desc by default, so the visible order already
// communicates standing — no dedicated rank column needed.
const DEFAULT_SORT: PayPerUseLeaderboardSort = {columnId: TOTAL_COLUMN_ID, direction: 'desc'};

const CELL_PADDING_VERTICAL = 3;
const CELL_PADDING_HORIZONTAL = 3;

const CSV_MIME_TYPE = 'text/csv;charset=utf-8';

// DELIBERATE PLATFORM-RULE EXCEPTION: the platform `Table` exposes no
// sticky-column / scroll-container API, so this wrapper reaches into its
// rendered `thead/tbody` cells via descendant selectors. This is a localized,
// documented trade-off (not a pattern to copy) pending a platform `Table`
// enhancement for pinned columns; revisit and remove once that lands.
//
// Horizontal-scroll container that keeps the first column (User, pinned left)
// and the last column (Total, pinned right) fixed while the operation-type
// columns scroll on narrow screens. `nowrap` lets the table outgrow the
// container so the scroll actually appears instead of squishing.
const ScrollableTable = styled.div`
  width: 100%;
  overflow-x: auto;

  th,
  td {
    white-space: nowrap;
  }

  /* Subtle header background for the scrollable (non-fixed) columns. The sticky
     first/last header rules below are more specific and keep their white fill. */
  thead th {
    background-color: ${({theme}) => theme.colors.palettes.neutral[10][100]};
  }

  thead th:first-child,
  tbody td:first-child,
  thead th:last-child,
  tbody td:last-child {
    position: sticky;
    z-index: 1;
    background-color: ${({theme}) => theme.colors.general.white};
  }

  /* Use an inset box-shadow (not border) for the divider so it stays painted
     above the scrolling cells — a plain border gets clipped during scroll. */
  thead th:first-child,
  tbody td:first-child {
    left: 0;
    box-shadow: inset -1px 0 0 0 ${({theme}) => theme.colors.general.separator};
  }

  thead th:last-child,
  tbody td:last-child {
    right: 0;
    box-shadow: inset 1px 0 0 0 ${({theme}) => theme.colors.general.separator};
  }

  tbody tr:hover td:first-child,
  tbody tr:hover td:last-child {
    background-color: ${({theme}) => theme.colors.palettes.neutral[20][100]};
  }

  /* The Total row is the last row in the body — give it (and its sticky cells)
     the same subtle fill as the header so it reads as a summary footer. */
  tbody tr:last-child td {
    background-color: ${({theme}) => theme.colors.palettes.neutral[10][100]};
  }
`;

interface LeaderboardCellProps {
  children: ReactNode;
}

// The platform table cell has no padding of its own, so each header/row cell
// pads its own content to keep rows comfortably spaced and aligned.
function LeaderboardCell(props: LeaderboardCellProps) {
  return (
    <Box paddingVertical={CELL_PADDING_VERTICAL} paddingHorizontal={CELL_PADDING_HORIZONTAL}>
      {props.children}
    </Box>
  );
}

interface PayPerUseLeaderboardTableProps extends RequiredTestIdProps {
  users: LeaderboardUser[];
  operationColumns: PayPerUseOperationColumn[];
  period: PayPerUsePeriod;
}

export function PayPerUseLeaderboardTable(props: PayPerUseLeaderboardTableProps) {
  const formatNumber = useFormatNumber();

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<PayPerUseLeaderboardSort>(DEFAULT_SORT);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(LEADERBOARD_ROWS_PER_PAGE);

  const handleSearch = (value: string | null) => {
    setSearch(value ?? '');
    setPage(1);
  };

  const handleRowsPerPageChange = (value: number) => {
    setRowsPerPage(value);
    setPage(1);
  };

  const handleSort = (columnId: string) => {
    setSort((previous) =>
      previous.columnId === columnId
        ? {columnId, direction: previous.direction === 'desc' ? 'asc' : 'desc'}
        : {columnId, direction: 'desc'}
    );
    setPage(1);
  };

  const handleToggleColumn = (key: string) => {
    setHiddenColumns((previous) =>
      previous.includes(key) ? previous.filter((column) => column !== key) : [...previous, key]
    );
  };

  const labeledColumns: LabeledOperationColumn[] = props.operationColumns.map((column) => ({
    ...column,
    label: match(getOperationTypeLabel(column))
      .with({kind: 'translation'}, (label) => i18n.t(label.translationKey))
      .with({kind: 'raw'}, (label) => label.text)
      .exhaustive(),
  }));
  const visibleColumns = labeledColumns.filter((column) => !hiddenColumns.includes(column.key));

  const columnVisibilityItems: ColumnVisibilityItem[] = labeledColumns.map((column) => ({
    key: column.key,
    label: column.label,
    isVisible: !hiddenColumns.includes(column.key),
  }));

  const filtered = filterLeaderboardUsers(props.users, search);
  const sorted = sortLeaderboardUsers(filtered, sort);
  const pageCount = getLeaderboardPageCount(sorted.length, rowsPerPage);
  const safePage = Math.min(page, pageCount);
  const rows = paginateLeaderboardUsers(sorted, safePage, rowsPerPage);

  const columnTotals = getLeaderboardColumnTotals(sorted, visibleColumns);
  const stats = getLeaderboardStats(sorted);
  const rangeFrom = sorted.length === 0 ? 0 : (safePage - 1) * rowsPerPage + 1;
  const rangeTo = Math.min(safePage * rowsPerPage, sorted.length);

  // Export the full filtered + sorted set (ignoring pagination) with the
  // currently visible columns — the CSV mirrors the user's view. The whole table
  // is already derived on the FE, so the CSV is generated client-side (no backend
  // round-trip) and saved straight to a file.
  const handleExport = () => {
    const table = buildLeaderboardExportTable({
      users: sorted,
      columns: visibleColumns,
      userColumnLabel: i18n.t('general.labels.user'),
      totalColumnLabel: i18n.t('general.labels.total'),
    });
    const csv = buildLeaderboardCsv(table);

    downloadBlobFile(new Blob([csv], {type: CSV_MIME_TYPE}), {
      fileName: buildLeaderboardExportFilename(props.period, new Date()),
      fileType: 'csv',
    });
  };

  const tableColumns: Column[] = [
    {
      element: (
        <LeaderboardCell>
          <Text size="small" alternative>
            {i18n.t('general.labels.user')}
          </Text>
        </LeaderboardCell>
      ),
    },
    ...visibleColumns.map((column) => ({
      element: (
        <LeaderboardCell>
          <PayPerUseSortableHeader
            label={column.label}
            columnId={column.key}
            sort={sort}
            onSort={handleSort}
            data-testid={suffixTestId(`header-${column.key}`, props)}
          />
        </LeaderboardCell>
      ),
    })),
    {
      element: (
        <LeaderboardCell>
          <PayPerUseSortableHeader
            label={i18n.t('general.labels.total')}
            columnId={TOTAL_COLUMN_ID}
            sort={sort}
            onSort={handleSort}
            data-testid={suffixTestId('header-total', props)}
          />
        </LeaderboardCell>
      ),
    },
  ];

  const statItems = [
    {id: 'sum', label: i18n.t('page.datagrid.labels.sum'), value: stats.sum},
    {id: 'average', label: i18n.t('general.labels.average'), value: stats.average},
    {id: 'min', label: i18n.t('page.datagrid.labels.min'), value: stats.min},
    {id: 'max', label: i18n.t('page.datagrid.labels.max'), value: stats.max},
  ];

  return (
    <Card>
      <VStack spacing={4}>
        <HStack spacing={4} justify="space-between" align="center">
          <Box width={80}>
            <Search
              value={search}
              onChange={handleSearch}
              onClear={() => handleSearch(null)}
              placeholder={i18n.t('page.payPerUseMetrics.leaderboard.searchPlaceholder')}
              data-testid={suffixTestId('search', props)}
            />
          </Box>
          <HStack spacing={3} align="center">
            <PayPerUseColumnVisibilityDropdown
              items={columnVisibilityItems}
              onToggle={handleToggleColumn}
              data-testid={suffixTestId('columnVisibility', props)}
            />
            <Button
              variant="secondary"
              leftIcon="file/download"
              title={i18n.t('page.datagrid.labels.exportCSV')}
              onClick={handleExport}
              isDisabled={sorted.length === 0}
              data-testid={suffixTestId('export', props)}
            />
          </HStack>
        </HStack>

        <DataStatus
          isEmpty={sorted.length === 0}
          emptyMessage={i18n.t('page.payPerUseMetrics.leaderboard.empty')}
          minHeight={40}
        >
          <ScrollableTable>
            <Table columns={tableColumns} data-testid={suffixTestId('table', props)}>
              {rows.map((user) => {
                const counts = zeroFillUserOperationCounts(user, visibleColumns);

                return (
                  <TableRow
                    key={user.userId}
                    data-testid={suffixTestId(`row-${user.userId}`, props)}
                  >
                    <LeaderboardCell>
                      <HStack spacing={2} align="center">
                        <Avatar
                          name={user.label}
                          size="small"
                          color="palettes.blue.20.100"
                          textColor="link"
                          data-testid={suffixTestId(`avatar-${user.userId}`, props)}
                        />
                        <Text size="small">{user.label}</Text>
                      </HStack>
                    </LeaderboardCell>
                    {visibleColumns.map((column) => (
                      <LeaderboardCell key={column.key}>
                        <Text size="small">{formatNumber(counts[column.key])}</Text>
                      </LeaderboardCell>
                    ))}
                    <LeaderboardCell>
                      <Text size="small" alternative>
                        {formatNumber(user.total)}
                      </Text>
                    </LeaderboardCell>
                  </TableRow>
                );
              })}
              <TableRow data-testid={suffixTestId('row-total', props)}>
                <LeaderboardCell>
                  <Text size="small" alternative>
                    {i18n.t('general.labels.total')}
                  </Text>
                </LeaderboardCell>
                {visibleColumns.map((column) => (
                  <LeaderboardCell key={column.key}>
                    <Text size="small" alternative>
                      {formatNumber(columnTotals[column.key])}
                    </Text>
                  </LeaderboardCell>
                ))}
                <LeaderboardCell>
                  <Text size="small" alternative>
                    {formatNumber(stats.sum)}
                  </Text>
                </LeaderboardCell>
              </TableRow>
            </Table>
          </ScrollableTable>
        </DataStatus>

        <Separator spacing={0} />

        <HStack justify="space-between" align="center" spacing={4}>
          <Text size="small" color="secondary">
            {i18n.t('page.payPerUseMetrics.leaderboard.range', {
              from: rangeFrom,
              to: rangeTo,
              total: sorted.length,
            })}
          </Text>
          <HStack spacing={4} align="center">
            {statItems.map((stat) => (
              <Text key={stat.id} size="small" color="secondary">
                {`${stat.label}: ${formatNumber(stat.value)}`}
              </Text>
            ))}
          </HStack>
        </HStack>

        <Separator spacing={0} />

        <HStack justify="space-between" align="center" spacing={4}>
          <HStack spacing={3} align="center">
            <Text size="small" color="secondary">
              {i18n.t('page.payPerUseMetrics.leaderboard.rowsPerPage')}
            </Text>
            <Dropdown
              dropdownControl={
                <Button
                  variant="secondary"
                  title={rowsPerPage.toString()}
                  rightIcon="navigation/expand_more"
                  data-testid={suffixTestId('rowsPerPage-trigger', props)}
                />
              }
              data-testid={suffixTestId('rowsPerPage', props)}
            >
              {LEADERBOARD_ROWS_PER_PAGE_OPTIONS.map((option) => (
                <DropdownItem
                  key={option}
                  label={option.toString()}
                  onClick={() => handleRowsPerPageChange(option)}
                  data-testid={suffixTestId(`rowsPerPage-item-${option}`, props)}
                />
              ))}
            </Dropdown>
            <Text size="small" color="secondary">
              {i18n.t('page.payPerUseMetrics.leaderboard.pageOf', {
                page: safePage,
                total: pageCount,
              })}
            </Text>
          </HStack>
          <Pagination
            pagesQuantity={pageCount}
            page={safePage}
            onPageChange={setPage}
            showFirstButton
            showLastButton
            showPrevButton
            showNextButton
            data-testid={suffixTestId('pagination', props)}
          />
        </HStack>
      </VStack>
    </Card>
  );
}
